import { useState } from 'react';

import Axios from 'axios';

import { forceReloadPackage } from '@hooks/API/Packages/UsePackages';

/**
 * Hook per gestire la logica business delle azioni sui package
 * Estratto da ModelsTableColumnPackage per evitare duplicazione
 */
export const usePackageActions = (
    model,
    context,
    newlyAddedToPackage,
    onAddingBooksToPackage,
    onRemovingBookFromPackage
) => {
    const { currentPackage, packageGroup } = context;
    const [isAddingFastPack, setIsAddingFastPack] = useState(0);
    const [isAddingCompCards, setIsAddingCompCards] = useState(0);
    const [isAddingFavourites, setIsAddingFavourites] = useState(0);
    const [refreshing, setRefreshing] = useState(false);

    const getModelPlaylists = (modelID) => {
        const modelPlaylistsInPackage = [];
        if (currentPackage && currentPackage.Models) {
            const found = currentPackage.Models.find((item) => item.ModelID === modelID);
            if (found && found.Playlists) {
                const playlistsInSavedPackage = found.Playlists.map((pl) => pl.PlaylistID);
                modelPlaylistsInPackage.push(...playlistsInSavedPackage);
            }
        }
        if (newlyAddedToPackage && newlyAddedToPackage.length > 0) {
            const found = newlyAddedToPackage.find((item) => item.modelID === modelID);
            if (found && found.playlistIDs && found.playlistIDs.length > 0) {
                const playlistsInNewlyAdded = found.playlistIDs;
                modelPlaylistsInPackage.push(...playlistsInNewlyAdded);
            }
        }
        return modelPlaylistsInPackage;
    };

    const getPlaylistCount = (model) => {
        if (currentPackage && currentPackage.Models) {
            let pc = 0;
            const found = currentPackage.Models.find((item) => item.ModelID === model.ID);
            const foundInNewlyAdded = newlyAddedToPackage.find((item) => item.modelID === model.ID);
            if (found && found.Playlists) {
                pc += found.Playlists.length;
            }
            if (foundInNewlyAdded) {
                pc += foundInNewlyAdded.playlistIDs.length;
            }
            return pc;
        }
        return 0;
    };

    /**
     * Una modella non può stare in due gruppi: se è già nel package sotto un gruppo
     * diverso da quello di destinazione, non la si può aggiungere. Vale anche quando la
     * destinazione è "ungrouped": un gruppo vuoto è comunque diverso da un gruppo pieno.
     * Torna il gruppo in conflitto, o null quando non ce n'è (modella non ancora nel
     * package, senza gruppo, o già in quello giusto).
     */
    const getModelGroupConflict = (model) => {
        if (!currentPackage || !currentPackage.Models) {
            return null;
        }
        const modelInPackage = currentPackage.Models.find((item) => item.ModelID === model.ID);
        const currentGroup = modelInPackage ? modelInPackage.Group : null;
        if (!currentGroup) {
            return null;
        }
        const destinationGroup = packageGroup || '';
        return currentGroup !== destinationGroup ? currentGroup : null;
    };

    const alreadyInPackage = (model) => {
        let modelPlaylistsIDs = [];
        const modelInSavedPackage = currentPackage.Models.find((packagemodel) => packagemodel.ModelID === model.ID);
        if (modelInSavedPackage) {
            modelPlaylistsIDs = modelInSavedPackage.Playlists.map((playlist) => playlist.PlaylistID);
        }
        let modelNewlyAddedPlaylistsIDs = [];
        const modelInNewlyAddedPackage = newlyAddedToPackage.find((item) => item.modelID === model.ID);
        if (modelInNewlyAddedPackage) {
            modelNewlyAddedPlaylistsIDs = modelInNewlyAddedPackage.playlistIDs;
        }
        //single array with distinct playlists IDs
        const allPlaylistsIDs = [...new Set([...modelPlaylistsIDs, ...modelNewlyAddedPlaylistsIDs])];
        const isInAllPlaylistsIDs = model.FP.every((playlistID) => allPlaylistsIDs.some((id) => id === playlistID));
        return isInAllPlaylistsIDs;
    };

    /**
     * I book indicati (dagli array della cache playlistsCount) sono già tutti nel package,
     * salvati o appena aggiunti: l'azione rapida non ha più nulla da fare.
     */
    const allPlaylistsInPackage = (model, playlistIDs) => {
        if (!playlistIDs || playlistIDs.length === 0) {
            return false;
        }
        const playlistsInPackage = getModelPlaylists(model.ID);
        return playlistIDs.every((playlistID) => playlistsInPackage.includes(playlistID));
    };

    const compCardsInPackage = (model) => allPlaylistsInPackage(model, model.CP);

    const favouritesInPackage = (model) => allPlaylistsInPackage(model, model.FAV);

    const handleAddBook = async (book) => {
        setRefreshing(true);
        try {
            const data = {
                packageID: currentPackage.ID,
                playlistID: book.ID,
                PackageGroup: packageGroup,
            };
            await Axios.post('/PackagePlaylists', data);
            onAddingBooksToPackage(book.ContactID, [book.ID]);
            setRefreshing(false);
        } catch (error) {
            console.error('Add Playlist To Package Error => ', error);
            setRefreshing(false);
        }
    };

    const handleDeleteBook = async (book) => {
        setRefreshing(true);
        const model = currentPackage.Models.find((model) => model.ModelID === book.ContactID);
        let foundInSavedPackage = false;
        if (model) {
            const playlist = model.Playlists.find((playlist) => playlist.PlaylistID === book.ID);
            if (playlist) {
                const packagePlaylistID = playlist.ID;
                foundInSavedPackage = true;
                try {
                    await Axios.delete(`/PackagePlaylists/${packagePlaylistID}`);
                    forceReloadPackage(currentPackage.ID);
                    setRefreshing(false);
                } catch (error) {
                    console.error('Delete Playlist To Package Error => ', error);
                    setRefreshing(false);
                }
            }
        }
        if (!foundInSavedPackage) {
            const foundInNewlyAddedPackage = newlyAddedToPackage.find((item) => item.modelID === book.ContactID);
            if (foundInNewlyAddedPackage) {
                const index = foundInNewlyAddedPackage.playlistIDs.indexOf(book.ID);
                if (index > -1) {
                    await Axios.delete(`/PackagePlaylists/byPlaylistID/${currentPackage.ID}/${book.ID}`);
                    setRefreshing(false);
                    onRemovingBookFromPackage(book.ContactID, book.ID);
                } else {
                    setRefreshing(false);
                }
            } else {
                setRefreshing(false);
            }
        }
    };

    const addFastPack = async (modelID) => {
        setIsAddingFastPack(modelID);
        var data = {
            PackageID: currentPackage.ID,
            Models: [modelID],
            PackageGroup: packageGroup,
        };
        const addedBookIds = await Axios.post('/PackagePlaylists/AddFastPackages', data);
        onAddingBooksToPackage(modelID, addedBookIds);
        setIsAddingFastPack(0);
    };

    /**
     * Aggiunge al package i book indicati. A differenza del fast package non c'è un
     * endpoint dedicato: si aggiunge un book alla volta, come handleAddBook.
     * I book già presenti nel package vengono saltati per non far fallire la POST.
     */
    const addPlaylistsOneByOne = async (model, playlistIDs, setIsAdding) => {
        if (!playlistIDs || playlistIDs.length === 0) {
            return;
        }
        const playlistsInPackage = getModelPlaylists(model.ID);
        const playlistsToAdd = playlistIDs.filter((playlistID) => !playlistsInPackage.includes(playlistID));
        if (playlistsToAdd.length === 0) {
            return;
        }
        setIsAdding(model.ID);
        const addedBookIds = [];
        try {
            for (const playlistID of playlistsToAdd) {
                const data = {
                    packageID: currentPackage.ID,
                    playlistID: playlistID,
                    PackageGroup: packageGroup,
                };
                await Axios.post('/PackagePlaylists', data);
                addedBookIds.push(playlistID);
            }
        } catch (error) {
            console.error('Add Playlists To Package Error => ', error);
        } finally {
            if (addedBookIds.length > 0) {
                onAddingBooksToPackage(model.ID, addedBookIds);
            }
            setIsAdding(0);
        }
    };

    const addCompCards = (model) => addPlaylistsOneByOne(model, model.CP, setIsAddingCompCards);

    const addFavourites = (model) => addPlaylistsOneByOne(model, model.FAV, setIsAddingFavourites);

    return {
        getModelPlaylists,
        getModelGroupConflict,
        getPlaylistCount,
        alreadyInPackage,
        compCardsInPackage,
        favouritesInPackage,
        handleAddBook,
        handleDeleteBook,
        addFastPack,
        addCompCards,
        addFavourites,
        isAddingFastPack,
        isAddingCompCards,
        isAddingFavourites,
        refreshing,
        currentPackage,
        packageGroup,
    };
};
